import { type FC } from 'react';
import { Check } from 'lucide-react';
import type { DayPass } from '@/hooks/config/useEventRegistrationSettings';

interface DayPassSelectorProps {
  passes: DayPass[];
  ownedIds: string[];
  /** Days already covered by a selected/owned bundle pass. Disabled, badged "Included". */
  coveredIds?: string[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

/**
 * Multi-select list of per-day attendance passes. Days the user already owns
 * are disabled with a "Purchased" badge; days already covered by a selected
 * or owned bundle pass are disabled with an "Included" badge; the rest toggle
 * like checkboxes.
 */
export const DayPassSelector: FC<DayPassSelectorProps> = ({ passes, ownedIds, coveredIds = [], selectedIds, onToggle }) => {
  // Which pass is subsuming this day? Named in the announcement so a screen reader says
  // "included in your Sat+Sun Pass" rather than a hardcoded (and one day false) "weekend
  // pass". Undefined when coveredIds came from somewhere no configured pass explains.
  const coveringPassFor = (id: string): DayPass | undefined =>
    passes.find(
      (p) =>
        p.id !== id &&
        (p.covers ?? []).includes(id) &&
        (selectedIds.includes(p.id) || ownedIds.includes(p.id)),
    );

  const selectedTotal = passes
    .filter((p) => {
      const isOwned = ownedIds.includes(p.id);
      const isCovered = !isOwned && coveredIds.includes(p.id);
      return !isOwned && !isCovered && selectedIds.includes(p.id);
    })
    .reduce((sum, p) => sum + p.fee, 0);

  return (
    <div className="space-y-4">
      {passes.map((pass) => {
        const isOwned = ownedIds.includes(pass.id);
        const isCovered = !isOwned && coveredIds.includes(pass.id);
        const isDisabled = isOwned || isCovered;
        const isSelected = !isDisabled && selectedIds.includes(pass.id);
        const coveringPass = isCovered ? coveringPassFor(pass.id) : undefined;
        const passFeeInDollars = pass.fee / 100;
        return (
          <button
            key={pass.id}
            onClick={() => {
              if (!isDisabled) onToggle(pass.id);
            }}
            disabled={isDisabled}
            className={`
              group relative w-full text-left transition-all duration-300 transform
              ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.02]'}
              ${isSelected ? 'scale-[1.02]' : ''}
            `}
            role="checkbox"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            aria-label={
              isOwned
                ? `${pass.name} - already purchased`
                : isCovered
                  ? coveringPass
                    ? `${pass.name} - included in your ${coveringPass.name}`
                    : `${pass.name} - included in a pass you already have`
                  : `${pass.name} - $${passFeeInDollars} single-day pass option`
            }
          >
            <div className={`
              relative rounded-xl overflow-hidden
              ${isSelected
                ? 'shadow-[0_0_30px_rgba(249,115,22,0.5)] ring-2 ring-orange-500'
                : 'shadow-lg'}
            `}>
              <div className={`
                p-4 bg-gradient-to-br flex items-center justify-between gap-4
                ${isSelected
                  ? 'from-orange-500 to-orange-600'
                  : 'from-turquoise-600 to-turquoise-700'}
              `}>
                <div className="flex items-center gap-3">
                  <div className={`
                    relative h-6 w-6 rounded-md flex items-center justify-center transition-all duration-300 flex-shrink-0
                    ${isSelected
                      ? 'bg-turquoise-500 shadow-[0_0_10px_rgba(20,184,166,0.5)]'
                      : 'bg-turquoise-800/50 border-2 border-orange-400'}
                  `}>
                    {isSelected && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                  </div>
                  <div>
                    <h4 className={`text-lg font-bold ${isSelected ? 'text-turquoise-100' : 'text-white'}`}>{pass.name}</h4>
                    {pass.description && (
                      <p className={`text-xs ${isSelected ? 'text-turquoise-100' : 'text-orange-200'}`}>{pass.description}</p>
                    )}
                  </div>
                </div>
                {isDisabled ? (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white flex-shrink-0 ${
                    isOwned ? 'bg-green-500/90' : 'bg-turquoise-500/90'
                  }`}>
                    {isOwned ? 'Purchased' : 'Included'}
                  </span>
                ) : (
                  <p className={`text-2xl font-black flex-shrink-0 ${isSelected ? 'text-turquoise-100' : 'text-orange-400'}`}>
                    ${passFeeInDollars}
                  </p>
                )}
              </div>
            </div>
          </button>
        );
      })}
      {selectedTotal > 0 && (
        <p className="text-right text-sm font-semibold text-orange-200">
          Day passes total: <span className="text-orange-400">${(selectedTotal / 100).toFixed(2)}</span>
        </p>
      )}
    </div>
  );
};
